import React from 'react';

import classNames from 'classnames';

import { formatMoneyWithLocale } from 'Helpers/formatService';

/**
 * UncommittedImpactHeader
 *
 * The headline of the Direct Commitment screen. For each balance it shows the
 * uncommitted impact split into cash and VIK separately:
 *   Uncommitted now  →  This commitment  →  Uncommitted after
 *
 * "Uncommitted after" is now − this commitment − what leaves the lines on approval:
 * a commitment below its budget returns the saving, so only the items it does not
 * commit stay uncommitted.
 *
 * Recomputes whenever the committing amount changes (after a per-type edit modal
 * save). A resulting negative balance is rendered in red. Cash-only lines only
 * show the cash figure; VIK
 * columns are hidden entirely when VIK is disabled for the event.
 *
 * Props:
 *  - context: { crumbs: string, line: string }
 *  - now:        { cash, vik }   uncommitted balance before this commitment
 *  - committing: { cash, vik }   amount being committed now
 *  - released:   { cash, vik }   saving that leaves the lines when this commitment is approved
 *  - vikEnabled: boolean
 *  - captions:   { now, committing, after } optional sub-line under each figure
 *  - lineUrl:    optional link for the budget line's name
 *  - contextLabel: optional label over the budget line; when given, the line's
 *                  location sits beneath its name and the line's column is wider
 */
/**
 * The committing figures are shown as the negative impact they have on the balance — this is
 * expenditure, so unlike the revenue-side heroes the red negative is correct here and stays.
 *
 * Negating through this rather than with a bare unary minus because `-0` is a real JavaScript
 * value and Intl.NumberFormat renders it as "-0", so a zero VIK component printed "VIK: -0".
 */
const asImpact = (value) => (value ? -value : 0);

export default function UncommittedImpactHeader({
    context = {},
    now = { cash: 0, vik: 0 },
    committing = { cash: 0, vik: 0 },
    released = { cash: 0, vik: 0 },
    vikEnabled = false,
    captions = {},
    lineUrl = null,
    contextLabel = null,
}) {
    const showVik = vikEnabled && ((now.vik ?? 0) !== 0 || (committing.vik ?? 0) !== 0);

    const after = {
        cash: (now.cash ?? 0) - (committing.cash ?? 0) - (released.cash ?? 0),
        vik: (now.vik ?? 0) - (committing.vik ?? 0) - (released.vik ?? 0),
    };

    // The prominent figure is the combined cash + VIK total; the cash and VIK breakdown
    // is shown as sub-lines beneath it (only when VIK applies — cash-only lines show just
    // the single figure, which already equals cash).
    const Stat = ({ label, cash, vik, caption = null, negative = false, highlight = false }) => {
        const combined = (cash ?? 0) + (vik ?? 0);
        return (
            <div className={classNames('tw-px-5 tw-py-4', { 'tw-bg-green-25/40': highlight })}>
                <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">{label}</div>
                <div className="tw-flex tw-flex-col tw-gap-y-0.5">
                    <div
                        className={classNames('tw-text-xl tw-font-bold tw-tabular-nums', {
                            'tw-text-red': negative && combined < 0,
                            'tw-text-charcoal': !(negative && combined < 0),
                        })}
                    >
                        {formatMoneyWithLocale(combined)}
                    </div>
                    {showVik && (
                        <>
                            <div
                                className={classNames('tw-text-sm tw-font-semibold tw-tabular-nums', {
                                    'tw-text-red': negative && cash < 0,
                                    'tw-text-charcoal-75': !(negative && cash < 0),
                                })}
                            >
                                {$t('Cash')}: {formatMoneyWithLocale(cash)}
                            </div>
                            <div
                                className={classNames('tw-text-sm tw-font-semibold tw-tabular-nums', {
                                    'tw-text-red': negative && vik < 0,
                                    'tw-text-charcoal-75': !(negative && vik < 0),
                                })}
                            >
                                {$t('VIK')}: {formatMoneyWithLocale(vik)}
                            </div>
                        </>
                    )}
                    {caption && <div className="tw-text-xs tw-text-charcoal-50">{caption}</div>}
                </div>
            </div>
        );
    };

    const crumbs = context.crumbs && (
        <div className={classNames('tw-text-xs tw-text-charcoal-50 tw-font-medium', contextLabel ? 'tw-mt-1' : 'tw-mb-1')}>{context.crumbs}</div>
    );

    return (
        <div
            className={classNames(
                'tw-grid tw-grid-cols-1 tw-divide-y md:tw-divide-y-0 md:tw-divide-x tw-divide-charcoal-15 tw-border tw-border-charcoal-15 tw-rounded-lg tw-overflow-hidden tw-bg-charcoal-10',
                contextLabel ? 'md:tw-grid-cols-[1.5fr_1fr_1fr_1fr]' : 'md:tw-grid-cols-4',
            )}
        >
            <div className="tw-px-5 tw-py-4">
                {contextLabel && <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">{contextLabel}</div>}
                {!contextLabel && crumbs}
                <div className="tw-text-lg tw-font-bold tw-text-charcoal">
                    {lineUrl ? (
                        <a href={lineUrl} target="_blank" rel="noopener noreferrer" className="tw-text-charcoal hover:tw-text-blue">
                            {context.line || '-'}
                        </a>
                    ) : (
                        context.line || '-'
                    )}
                </div>
                {contextLabel && crumbs}
            </div>
            <Stat label={$t('Uncommitted now')} cash={now.cash ?? 0} vik={now.vik ?? 0} caption={captions.now} />
            <Stat
                label={$t('This commitment')}
                cash={asImpact(committing.cash ?? 0)}
                vik={asImpact(committing.vik ?? 0)}
                caption={captions.committing}
                negative
            />
            <Stat label={$t('Uncommitted after')} cash={after.cash} vik={after.vik} caption={captions.after} negative highlight />
        </div>
    );
}
